<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { CreateArticleDTOMeasurement } from 'src/api/models/createArticleDTOMeasurement';
import { formatCurrency } from 'src/utils/currency';
import { calcSalesPrice } from 'src/utils/article-utils';

interface ArticleFormData {
  name: string;
  description: string | null;
  measurement: string;
  minimum_margin: number;
  sale_price: number | null;
  internal_id: number | null;
}

interface Props {
  modelValue: ArticleFormData;
  purchaseNetPrice: number | null;
  disabled?: boolean;
}

const { modelValue, purchaseNetPrice = null, disabled = false } = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: ArticleFormData];
}>();

const form = ref<ArticleFormData>({ ...modelValue });
let syncingFromParent = false;

watch(() => modelValue, (val) => {
  if (syncingFromParent) return;
  syncingFromParent = true;
  form.value = { ...val };
  syncingFromParent = false;
}, { deep: true });

function onFieldChange () {
  emit('update:modelValue', { ...form.value });
}

const calculatedSalePrice = computed(() => {
  if (purchaseNetPrice == null || purchaseNetPrice <= 0) return form.value.sale_price ?? 0;
  return calcSalesPrice(purchaseNetPrice, form.value.minimum_margin);
});

function onMarginInput (e: { value: string | number | undefined }) {
  form.value.minimum_margin = Number(e.value) || 0;
  form.value.sale_price = calculatedSalePrice.value;
  onFieldChange();
}

const measurementOptions = [
  { label: 'Per stuk', value: CreateArticleDTOMeasurement.piece },
  { label: 'Per liter', value: CreateArticleDTOMeasurement.liter },
  { label: 'Per kg', value: CreateArticleDTOMeasurement.kilogram },
];
</script>

<template>
  <div data-testid="article-form-fields" class="flex flex-col gap-4">
    <div>
      <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Identity</span>
      <div class="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-color-secondary">Name *</label>
          <InputText
            data-testid="article-field-name"
            v-model="form.name"
            :disabled="disabled"
            size="small"
            fluid
            @update:model-value="onFieldChange"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-color-secondary">Measurement *</label>
          <Select
            data-testid="article-field-measurement"
            v-model="form.measurement"
            :options="measurementOptions"
            option-label="label"
            option-value="value"
            :disabled="disabled"
            size="small"
            fluid
            @update:model-value="onFieldChange"
          />
        </div>

        <div class="col-span-2 flex flex-col gap-1">
          <label class="text-sm font-medium text-color-secondary">Description</label>
          <Textarea
            data-testid="article-field-description"
            v-model="form.description"
            :disabled="disabled"
            size="small"
            fluid
            rows="2"
            auto-resize
            @update:model-value="onFieldChange"
          />
        </div>
      </div>
    </div>

    <div>
      <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Pricing</span>
      <div class="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-color-secondary">Minimum margin *</label>
          <InputNumber
            data-testid="article-field-minimum-margin"
            v-model="form.minimum_margin"
            :disabled="disabled"
            suffix="%"
            :min="0"
            size="small"
            fluid
            @input="onMarginInput"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-color-secondary">Sale price</label>
          <div data-testid="article-field-sale-price" class="font-mono text-sm px-2.5 py-1.5 border border-surface-300 dark:border-surface-600 rounded-md bg-surface-100 dark:bg-surface-800 text-surface-500 dark:text-surface-400">
            {{ formatCurrency(calculatedSalePrice) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
